/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IDrawConditionProps,
  IPostSurveyAddQuestionsProps,
  IPostSurveyAddSaveFormattedProps,
  IProjectInfoAction,
  IProjectInfoInitialStateProps,
  Question,
} from 'interface/project/projectInfo';
import set from 'lodash/set';
import uniqueId from 'lodash/uniqueId';
import cloneDeep from 'lodash/cloneDeep';

export const projectInfoInitalState: IProjectInfoInitialStateProps = {
  inputs: {
    projectType: '',
    projectName: '',
    projectStartAt: null,
    projectEndAt: null,
    compName: '',
    compAdminList: [],
    activeStep: 0,
    completed: {},
    isStep1Valid: false,
    projectId: '',
    projectPartUserFileName: '',
    userExcel: {
      file: null,
      rowCount: 0,
      excelData: [],
      action: '',
    },
    stepper2: {
      id: '',
      serverId: '',
    },
    stepper3: {
      questions: [],
      noAdditional: false,
      isQuestionLoaded: false,
    },
    stepper4: {
      projectDescription: '',
      projectAgreement: '',
      completeMessage: '',
      emailTitle: '',
      emailBody: '',
    },
  },
  dcInputs: {
    projectId: '',
    projectType: '',
    inspectionId: '',
    activeStep: 0,
    completed: {},
    stepper1: {
      projectName: '',
      projectStartAt: null,
      projectEndAt: null,
      compName: '',
      compAdminList: [],
      isStep1Valid: false,
      projectPartUserFileName: '',
      userExcel: {
        file: null,
        rowCount: 0,
        excelData: [],
      },
      ipsativeExcel: {
        file: null,
        rowCount: 0,
        excelData: [],
      },
    },
    stepper2: {
      minus6: '0',
      minus5: '0',
      minus4: '0',
      minus3: '0',
      minus2: '0',
      minus1: '0',
      zero: '0',
      plus1: '0',
      plus2: '0',
      plus3: '0',
      plus4: '0',
      plus5: '0',
      plus6: '0',
    },
    stepper2Update: false,
    stepper3: {
      questions: [],
      noAdditional: false,
      isQuestionLoaded: false,
    },
    stepper4: {
      projectDescription: '',
      projectAgreement: '',
      completeMessage: '',
      emailTitle: '',
      emailBody: '',
    },
  },
};

const handleChangeByType = (
  state: IProjectInfoInitialStateProps,
  type: 'inputs' | 'dcInputs',
  path: string,
  value: any,
): IProjectInfoInitialStateProps => {
  // const updatedSection = { ...state[type] };
  const updatedSection = cloneDeep(state[type]);
  set(updatedSection, path, value);

  return {
    ...state,
    [type]: updatedSection,
  };
};

export const projectInfoReducer = (
  state: IProjectInfoInitialStateProps,
  action: IProjectInfoAction,
): IProjectInfoInitialStateProps => {
  switch (action.type) {
    case 'reset': {
      return {
        ...projectInfoInitalState,
        inputs: {
          ...projectInfoInitalState.inputs,
          projectType: state.inputs.projectType,
        },
        dcInputs: {
          ...projectInfoInitalState.dcInputs,
          projectType: state.dcInputs.projectType,
        },
      };
    }
    case 'handleChangeByType': {
      const { type, name, value } = action.payload;
      return handleChangeByType(state, type, name, value);
    }
    case 'handleDetailData': {
      const { ...others } = action.payload;
      return {
        ...state,
        inputs: {
          ...state.inputs,
          ...others,
        },
      };
    }
    case 'handleDetailDataByDC': {
      const { projectId, projectType, ...others } = action.payload;
      return {
        ...state,
        dcInputs: {
          ...state.dcInputs,
          projectId,
          projectType,
          stepper1: {
            ...state.dcInputs.stepper1,
            ...others,
          },
        },
      };
    }
    case 'handleQuestion': {
      const { type, questionId, question, questionType, items, actionType, contentId, content } = action.payload;
      let updatedQuestions = [...state[type as 'inputs' | 'dcInputs'].stepper3.questions];

      switch (actionType) {
        case 'update':
          updatedQuestions = updatedQuestions.map(q =>
            q.questionId === questionId
              ? {
                  ...q,
                  question: question ?? q.question,
                  questionType: questionType ?? q.questionType,
                  items: items ?? q.items,
                }
              : q,
          );
          break;

        case 'add':
          updatedQuestions.push({
            questionId: uniqueId(),
            question: question ?? '',
            questionType: questionType ?? '',
            items: ['OC', 'LK'].includes(questionType) ? [{ contentId: `content-${uniqueId()}`, content: '' }] : [],
          });
          break;

        case 'delete':
          updatedQuestions = updatedQuestions.filter(q => q.questionId !== questionId);
          break;
        case 'addItem':
          updatedQuestions = updatedQuestions.map(q =>
            q.questionId === questionId
              ? {
                  ...q,
                  items: [...q.items, { contentId: `content-${uniqueId()}`, content: content ?? '' }],
                }
              : q,
          );
          break;

        case 'updateItem':
          updatedQuestions = updatedQuestions.map(q =>
            q.questionId === questionId
              ? {
                  ...q,
                  items: q.items.map(item =>
                    item.contentId === contentId ? { ...item, content: content ?? item.content } : item,
                  ),
                }
              : q,
          );
          break;

        case 'deleteItem':
          updatedQuestions = updatedQuestions.map(q =>
            q.questionId === questionId
              ? {
                  ...q,
                  items: q.items.filter(item => item.contentId !== contentId),
                }
              : q,
          );
          break;
        default:
          break;
      }

      return {
        ...state,
        [type]: {
          ...state[type as 'inputs' | 'dcInputs'],
          stepper3: {
            ...state[type as 'inputs' | 'dcInputs'].stepper3,
            questions: updatedQuestions,
          },
        },
      };
    }
    case 'handleQuestionLoadData': {
      const { type, questions } = action.payload as {
        type: 'inputs' | 'dcInputs';
        questions: Question[];
      };
      const tmpQuestions = questions.map(q => ({
        questionId: q.id,
        question: q.question,
        questionType: q.questionType,
        items: q.items.map(item => ({
          contentId: item.id,
          content: item.content,
        })),
      }));
      return {
        ...state,
        [type]: {
          ...state[type],
          stepper3: {
            ...state[type].stepper3,
            questions: tmpQuestions || [],
            isQuestionLoaded: true,
          },
        },
      };
    }
    default:
      return state;
  }
};

export const handleSurveyAdditionFormat = (
  tmpData: IPostSurveyAddQuestionsProps[],
): IPostSurveyAddSaveFormattedProps => {
  const tmp = tmpData.map(obj => ({
    question: obj.question,
    questionType: obj.questionType,
    items: obj.items.map(item => ({
      content: item.content,
    })),
  }));
  return {
    questions: tmp,
  };
};

export const drawGridCondition: IDrawConditionProps[] = [
  { id: 'minus6', color: '#C62A2A', label: '-6' },
  { id: 'minus5', color: '#D65555', label: '-5' },
  { id: 'minus4', color: '#E28181', label: '-4' },
  { id: 'minus3', color: '#E9B5B5', label: '-3' },
  { id: 'minus2', color: '#F2D5D5', label: '-2' },
  { id: 'minus1', color: '#F8E8E8', label: '-1' },
  { id: 'zero', color: '#F3F4F6', label: '0' },
  { id: 'plus1', color: '#E4F8EA', label: '1' },
  { id: 'plus2', color: '#C8F3D7', label: '2' },
  { id: 'plus3', color: '#9EE5B6', label: '3' },
  { id: 'plus4', color: '#68CA83', label: '4' },
  { id: 'plus5', color: '#4DA465', label: '5' },
  { id: 'plus6', color: '#397E4E', label: '6' },
];
